import React, { useState, useEffect } from "react";
import { useProperty } from "../context/PropertyContext";
import { IP } from '../configuracion.js';
import { useLanguage } from "../context/LanguageContext";
import bcrypt from "bcryptjs";
import "../styles/Login.css";
import logo from "../_lib/tavpos.png";
import loginPubli from "../_lib/login_publi.jpeg";
// Botón inteligente de sincronización de JSON (ver
// Analisis_Viabilidad_Sync_JSON_Login.docx). Módulo aislado: no afecta
// ninguna funcionalidad existente de Login si compararConNube falla o
// nunca se resuelve (ver manejo de errores silencioso más abajo).
import { compararConNube, sincronizarDiferencias } from "../utils/jsonSync.js";

const BASE_API_URL = `${IP}/tavpos/backend`;

// Requerimiento: employees.json puede traer password en texto plano (como
// hoy) o como hash bcrypt (nuevo formato, ej. generado con password_hash()
// de PHP). Se detecta el formato por su forma — un hash bcrypt siempre
// empieza con $2a$/$2b$/$2y$ seguido del costo de dos dígitos y otro $,
// algo que un PIN de 3-4 dígitos jamás podría producir por accidente. Si no
// coincide con ese patrón, se compara como texto plano exactamente igual
// que antes — cero cambio de comportamiento para los usuarios actuales.
const BCRYPT_PATTERN = /^\$2[aby]\$\d{2}\$/;
const verifyPassword = (inputPassword, storedPassword) => {
  if (typeof storedPassword === "string" && BCRYPT_PATTERN.test(storedPassword)) {
    try {
      return bcrypt.compareSync(inputPassword, storedPassword);
    } catch (error) {
      return false;
    }
  }
  return storedPassword === inputPassword;
};

const Login = ({ onLoginSuccess }) => {
  const [usr, setUsr] = useState("");
  const [password, setPassword] = useState("");
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const { property, setProperty } = useProperty();

  // Usar solo useLanguage sin diagnóstico
  const { t } = useLanguage();

  // ===== Botón inteligente de sincronización de JSON (aislado) =====
  // Botón SIEMPRE visible, con color fijo salvo cuando hay cambios (naranja).
  // syncStatus:
  //   'idle'     -> color normal. Tocar = solo verificar contra la nube.
  //   'checking' -> verificando (color normal, deshabilitado).
  //   'pending'  -> hay cambios detectados, botón en naranja. Tocar = sincronizar.
  //   'syncing'  -> sincronizando (naranja, deshabilitado).
  //   'error'    -> falló la sincronización (rojo). Tocar = reintentar.
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncFiles, setSyncFiles] = useState([]);
  const [syncProgressMsg, setSyncProgressMsg] = useState("");
  const [syncErrorMsg, setSyncErrorMsg] = useState(null); // { file, message } | null

  // Sincronizar propiedad con localStorage
  useEffect(() => {
    const savedProperty = localStorage.getItem("currentProperty");
    if (savedProperty && !property) {
      setProperty(savedProperty);
    }
  }, [property, setProperty]);

  // Comparación silenciosa contra la nube (botón de sincronización de JSON).
  // No bloquea ni altera el resto del login: si falla, solo se registra en
  // consola y el botón se queda en su color normal (no molesta a nadie).
  //
  // Se repite sola cada pocos minutos (SYNC_CHECK_INTERVAL_MS) mientras la
  // pantalla de Login esté visible, además de al montar. Esto es necesario
  // para instalaciones tipo WAP/PWA en un dispositivo POS: ahí no existe un
  // botón de "recargar página" del navegador, así que no podemos depender de
  // que alguien refresque manualmente para que se note un cambio — el
  // chequeo tiene que dispararse solo, y si encuentra cambios pone el botón
  // en naranja para avisar.
  useEffect(() => {
    if (!property) return;
    let cancelled = false;

    const check = () => {
      compararConNube(property)
        .then((diffs) => {
          if (cancelled) return;
          setSyncFiles(diffs);
          // No pisar el color mientras se está sincronizando o hubo un error
          // que el usuario todavía no vio/reintentó.
          setSyncStatus((prev) => {
            if (prev === 'syncing') return prev;
            return diffs.length > 0 ? 'pending' : 'idle';
          });
        })
        .catch((err) => {
          console.warn("[jsonSync] Comparación silenciosa no disponible:", err.message);
        });
    };

    check(); // primer chequeo al mostrar Login (comportamiento original, sin cambios)

    const SYNC_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos
    const intervalId = setInterval(check, SYNC_CHECK_INTERVAL_MS);

    // También al volver a primer plano (útil en PWA/WAP: el usuario cambia
    // de app o apaga la pantalla y vuelve, sin que la página se recargue).
    const handleVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [property]);

  // Tocar el botón: comportamiento distinto según el color/estado actual.
  //  - Normal (idle): solo verifica si hay cambios. Si hay, se pone naranja.
  //  - Naranja (pending) o rojo (error): sincroniza. Al terminar bien, vuelve
  //    a su color normal.
  const handleSyncButtonClick = async () => {
    if (syncStatus === 'checking' || syncStatus === 'syncing') return;

    if (syncStatus === 'idle') {
      setSyncStatus('checking');
      try {
        const diffs = await compararConNube(property);
        setSyncFiles(diffs);
        setSyncStatus(diffs.length > 0 ? 'pending' : 'idle');
      } catch (err) {
        console.warn("[jsonSync] Verificación manual no disponible:", err.message);
        setSyncStatus('idle');
      }
      return;
    }

    // pending o error -> sincronizar
    setSyncStatus('syncing');
    setSyncErrorMsg(null);
    try {
      await sincronizarDiferencias(property, syncFiles, (msg) => setSyncProgressMsg(msg));
      setSyncFiles([]);
      setSyncStatus('idle');
    } catch (err) {
      setSyncErrorMsg({ file: err.file || "desconocido", message: err.message });
      setSyncStatus('error');
    } finally {
      setSyncProgressMsg("");
    }
  };

  // Cargar empleados desde el backend
  useEffect(() => {
    let isMounted = true;
    
    if (!property) {
      if (isMounted) setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const loadEmployees = async () => {
      try {
        const url = `${BASE_API_URL}/data/${property}/employees.json`;
        
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        
        if (!text.trim()) {
          throw new Error("Respuesta vacía del servidor");
        }
        
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          // Intentar arreglar JSON malformado
          const fixedText = text
            .replace(/,\s*}/g, '}')
            .replace(/,\s*]/g, ']')
            .replace(/\/\/.*$/gm, '')
            .trim();
          
          try {
            data = JSON.parse(fixedText);
          } catch (fixError) {
            throw new Error("Formato JSON inválido en employees.json");
          }
        }
        
        if (isMounted) {
          if (Array.isArray(data)) {
            const employeesWithLanguage = data.map(emp => ({
              ...emp,
              idioma: emp.idioma || "ES"
            }));
            setEmployees(employeesWithLanguage);
          } else if (data.empleados && Array.isArray(data.empleados)) {
            const employeesWithLanguage = data.empleados.map(emp => ({
              ...emp,
              idioma: emp.idioma || "ES"
            }));
            setEmployees(employeesWithLanguage);
          } else {
            setEmployees([]);
          }
          setError("");
        }
      } catch (error) {
        if (isMounted) {
          console.error(`Error cargando empleados:`, error);
          setError(
            t('error_loading_employees', `Error al cargar datos de empleados para ${property}.`)
          );
          setEmployees([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadEmployees();

    return () => {
      isMounted = false;
    };
  }, [property, retryCount, t]);

  useEffect(() => {
    document.body.classList.add("body-login-active");
    return () => {
      document.body.classList.remove("body-login-active");
    };
  }, []);

  const handleNumberClick = (number) => {
    if (!showPasswordInput) {
      setUsr((prev) => (prev.length < 10 ? prev + number : prev));
    } else {
      setPassword((prev) => (prev.length < 10 ? prev + number : prev));
    }
  };

  const handleBackspace = () => {
    if (!showPasswordInput) {
      setUsr((prev) => prev.slice(0, -1));
    } else {
      setPassword((prev) => prev.slice(0, -1));
    }
  };

const handleEnter = async () => {
  if (loading || employees.length === 0) return;

  if (!showPasswordInput) {
    if (usr.length < 4) {
      setError(t('min_user_length', "El usuario debe tener al menos 4 dígitos"));
      return;
    }
    setShowPasswordInput(true);
    setError("");
  } else {
    try {
      const employee = employees.find(emp => {
        const usrMatch = emp.usr && emp.usr.toString() === usr;
        const cardMatch = emp.card && emp.card === usr;
        return (usrMatch || cardMatch) && verifyPassword(password, emp.password);
      });

      if (employee) {
        const userLanguage = employee.idioma || "ES";
        
        const userWithLanguage = {
          id: employee.id,
          nombre: employee.nombre,
          usr: employee.usr,
          password: employee.password,
          card: employee.card,
          admin: employee.admin,
          seguridad: Number(employee.seguridad) || 4,
          idioma: userLanguage,
          property: property
        };
        
        // Limpiar estados locales
        setUsr("");
        setPassword("");
        setShowPasswordInput(false);
        setError("");
        
        document.body.classList.remove("body-login-active");
        
        // Pequeño delay antes de llamar a onLoginSuccess
        setTimeout(() => {
          onLoginSuccess(userWithLanguage);
        }, 50);
      } else {
        throw new Error(t('invalid_credentials', "Credenciales incorrectas"));
      }
    } catch (error) {
      setError(error.message);
      setTimeout(() => {
        setUsr("");
        setPassword("");
        setShowPasswordInput(false);
        setError("");
      }, 2000);
    }
  }
};

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key >= "0" && e.key <= "9") {
      handleNumberClick(e.key);
    } else if (e.key === "Backspace") {
      handleBackspace();
    } else if (e.key === "Enter") {
      handleEnter();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [usr, password, showPasswordInput, employees, loading]);

  // Loading simplificado
  if (loading) {
    return (
      <div className="login-root">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('loading_employees', `Cargando empleados para ${property}...`)}</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="login-root">
        <div className="error-container">
          <h2>{t('system_error', "Error en el sistema")}</h2>
          <p>{t('no_property_selected', "No se ha seleccionado una propiedad.")}</p>
          <button
            onClick={() => {
              localStorage.removeItem("currentDeviceConfig");
              localStorage.removeItem("currentProperty");
              window.location.reload();
            }}
            className="retry-button"
          >
            {t('configure_device', "Configurar Dispositivo")}
          </button>
        </div>
      </div>
    );
  }

  if (error && !employees.length) {
    return (
      <div className="login-root">
        <div className="error-container">
          <h2>{t('system_error', "Error en el sistema")}</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={handleRetry} className="retry-button">
              {t('retry', "Reintentar")}
            </button>
            <p className="property-info">
              {t('property', "Propiedad")}: {property}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-root">
      <div className="split-container">
        <div className="left-section">
          <img src={loginPubli} alt="Background" className="background-image" />
          <div className="overlay">
            <h2>{t('we_make_simple', "Hacemos simple lo complejo")}</h2>

            {/* Botón fijo, siempre visible. Texto fijo en español (no vía t()):
                son claves nuevas que no existen en los XML de idioma de las
                propiedades, y t() no tiene fallback real (ignora el segundo
                argumento si la clave no está registrada). */}
            <div className="json-sync-box">
              <button
                className={
                  "json-sync-button" +
                  (syncStatus === 'pending' ? " json-sync-button--pending" : "") +
                  (syncStatus === 'syncing' ? " json-sync-button--syncing" : "") +
                  (syncStatus === 'error' ? " json-sync-button--error" : "")
                }
                onClick={handleSyncButtonClick}
                disabled={syncStatus === 'checking' || syncStatus === 'syncing'}
                aria-label="Actualizar configuración"
              >
                {syncStatus === 'checking' && "Verificando..."}
                {syncStatus === 'syncing' && (syncProgressMsg || "Sincronizando...")}
                {syncStatus === 'pending' && "Hay cambios: tocar para sincronizar"}
                {syncStatus === 'error' && "Error al sincronizar: tocar para reintentar"}
                {syncStatus === 'idle' && "Actualizar configuración"}
              </button>

              {syncStatus === 'error' && syncErrorMsg && (
                <p className="json-sync-error-detail">
                  {`(${syncErrorMsg.file}: ${syncErrorMsg.message})`}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="right-section">
          <div className="login-container">
            <a href="https://tavgol.com/tavportal/login" target="_blank" rel="noopener noreferrer">
              <img src={logo} alt="Logo TAV Inventarios" className="logo" />
            </a>
            <h1>{t('login', "Iniciar Sesión")}</h1>
            <p className="property-subtitle">
              {property}
            </p>

            {error && (
              <div className="error-message animate__animated animate__headShake">
                {error}
              </div>
            )}

            {/* ===== INPUTS REEMPLAZABLES ===== */}
            <div className="input-fields">
              {/* Input de usuario - visible solo cuando NO showPasswordInput */}
              <div className={`input-field-container user-field ${!showPasswordInput ? '' : 'hide'}`}>
                <input
                  type="text"
                  value={usr}
                  readOnly
                  className="form-control"
                  placeholder={t('user', "Usuario")}
                  aria-label={t('user_field', "Campo de usuario")}
                />
              </div>
              
              {/* Input de password - visible solo cuando showPasswordInput */}
              <div className={`input-field-container password-field ${showPasswordInput ? 'show' : ''}`}>
                <input
                  type="password"
                  value={password}
                  readOnly
                  className="form-control"
                  placeholder={t('password', "Contraseña")}
                  aria-label={t('password_field', "Campo de contraseña")}
                />
              </div>
            </div>

            <div className="keyboard">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
                <button
                  key={number}
                  onClick={() => handleNumberClick(number)}
                  className="btn-keyboard"
                  aria-label={t('number', `Número ${number}`)}
                >
                  {number}
                </button>
              ))}
              <button
                onClick={() => handleNumberClick(0)}
                className="btn-keyboard"
                aria-label={t('number_zero', "Número 0")}
              >
                0
              </button>
              <button
                onClick={handleBackspace}
                className="btn-keyboard special-key"
                aria-label={t('delete', "Borrar")}
              >
                ⌫
              </button>
              <button
                onClick={handleEnter}
                className="btn-primary"
                disabled={
                  loading ||
                  (!showPasswordInput && usr.length < 4) ||
                  (showPasswordInput && password.length < 4)
                }
              >
                {showPasswordInput ? t('enter', "Entrar") : t('continue', "Continuar")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;